import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { CommonAddressResponse } from '../../../core/models/CommonAddressResponse';
import { ChannelService } from 'src/app/home/channel/channel.service';
import { ErrorLocation } from 'src/app/core/enums/ErrorLocation';
import { UserQuery } from 'src/app/core/user/user.query';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from 'src/app/core/user/user.service';
import { AddressService } from '../../services/address.service';
import { AddrSysFlag } from './../../../core/enums/AddrSysFlag';
import { LocationCoordinatorService } from '../../services/location-coordinator.service';

@Component({
  selector: 'app-address-search',
  templateUrl: './address-search.component.html',
  styleUrls: ['./address-search.component.scss']
})
export class AddressSearchComponent implements OnInit {

  @ViewChild("addressSearch", { static: false }) addressSearchElementRef: ElementRef | any;
  @Input() set searchValue(address: string) {
    this.addressSearchValue = address;
  };

  get searchValue() {
    return this.addressSearchValue;
  }
  @Input() addressType : string;
  @Input() selectedChannelTag: string;
  @Input() selectedChannelId: number;
  @Input() isAddNewAddress: boolean;
  @Input() events: Observable<void>;
  @Input () errorIcon : boolean = false;
  @Input() isHomePage : boolean =false;
  @Output() outputSelectedAddress = new EventEmitter<CommonAddressResponse>();
  @Output() errorLocationCode = new EventEmitter<number>();


  modelChanged: Subject<string> = new Subject<string>();
  placeList: CommonAddressResponse[] = [];
  allPredefinedPlaces: CommonAddressResponse[] = [];
  displayPlaceLayout: boolean = false;
  addressSearchValue: string = '';
  locationNotFound: boolean = false;
  selectedAddress: CommonAddressResponse;
  addressSearchError: HttpErrorResponse = null;
  resetAddressSubscription$: Subscription = new Subscription();
  addressSub$: Subscription = new Subscription();
  stopDebounce: boolean = false;
  isCurrentLocationLoading: boolean = false;

  eAddrSysFlag = AddrSysFlag;

  constructor(
    private channelService: ChannelService,
    private userQuery: UserQuery,
    private userService: UserService,
    private addressService: AddressService,
    private locationCoordinator: LocationCoordinatorService,
  ) {
    this.modelChanged.pipe(
      debounce(() => timer(this.addressType === AddrSysFlag.Predefined ? 100 : 1000)))
      .subscribe(async searchText => {
        if (!this.stopDebounce) {
          if (this.addressType === AddrSysFlag.Predefined) {
            this.filterPredefinedPlaces(searchText);
          } else {
            await this.getPlace(searchText);
            this.errorIcon = false;
          }
        }
      });
  }

  async ngOnInit(): Promise<void> {
    if (!this.isAddNewAddress) {
      this.addressSub$ = this.userQuery.selectedAddress$.subscribe(address => {
        if (address) {
          this.selectedAddress = address;
          if (!this.searchValue) {
            this.addressSearchValue = address.fullAddress;
          }
        } else if (!address && this.searchValue) {
          this.addressSearchValue = this.searchValue
        }
        else {
          this.clearAddressField();
        }
      });
    }

    if(this.addressType == AddrSysFlag.Predefined){
      await this.getPlace('');
    }

    if (this.events) {
      this.resetAddressSubscription$ = this.events.subscribe(() => this.removeAddress());
    }
  }

  changed(text: string) {
    this.stopDebounce = false;
    this.modelChanged.next(text);
  }

  async selectAddress(addressId: string, type: string): Promise<void> {
    try {
      const data = await this.channelService.getPlaceDetail(addressId, type);
      if (data) {
        this.applyResolvedAddress(data);
      }
    } catch (error) {
      console.log(error)
    }
  }

  async searchCurrentLocation(): Promise<void> {
    if (this.isCurrentLocationLoading) {
      return;
    }

    this.isCurrentLocationLoading = true;
    try {
      const address = await this.locationCoordinator.getManualLocationAddress(this.selectedChannelId);

      if (address) {
        this.applyResolvedAddress(address);
      } else {
        this.errorLocationCode.emit(ErrorLocation.NOT_AVAILABLE);
      }
    } catch (error) {
      this.locationNotFound = false;
      this.errorIcon = false;
      if (error?.code === ErrorLocation.BLOCK_GPS) {
        this.addressService.showEnablePermissionDialog();
        this.errorLocationCode.emit(ErrorLocation.BLOCK_GPS);
      } else {
        console.log(error)
        this.errorLocationCode.emit(ErrorLocation.NOT_AVAILABLE);
      }
    } finally {
      this.isCurrentLocationLoading = false;
    }
  }

  private applyResolvedAddress(address: CommonAddressResponse): void {
    localStorage.setItem('recent-address', JSON.stringify(address));
    this.displayPlaceLayout = false;
    this.addressSearchValue = address.fullAddress;
    this.outputSelectedAddress.emit(address);
    this.errorLocationCode.emit(ErrorLocation.NONE);
    this.userService.setRecentAddress();
  }

  removeAddress() {
    this.clearAddressField();

    if (this.addressType === AddrSysFlag.Predefined) {
      this.filterPredefinedPlaces('');
    }

    this.errorLocationCode.emit(ErrorLocation.EMPTY_ADDRESS);
  }

  clearAddressField() {
    this.addressSearchValue = '';
    this.locationNotFound = false;
    this.errorIcon = false;
  }

  ngOnDestroy() {
    this.resetAddressSubscription$?.unsubscribe();
    this.addressSub$?.unsubscribe()
  }

  async getPlace(searchText: string) {
    if (searchText || searchText == '') {
      let channelTag = this.selectedChannelTag ? this.selectedChannelTag : this.channelService.getChannelData()?.channelTag;
      this.channelService.getPlaceAutoComplete(channelTag, searchText).then(data => {
        if (data instanceof HttpErrorResponse === false) {
          this.placeList = data;
          if (this.addressType === AddrSysFlag.Predefined && !searchText) {
            this.allPredefinedPlaces = data;
          }
          if (this.placeList.length === 0) {
            this.displayPlaceLayout = false;
            this.locationNotFound = true;
            this.errorIcon = true;
          } else {
            this.displayPlaceLayout = true;
            this.locationNotFound = false;
            this.errorIcon = false;
          }
        }
      }).catch(error => {
        this.placeList = [];
        this.displayPlaceLayout = false;
        this.addressSearchError = error;
      });
      this.errorLocationCode.emit(ErrorLocation.NONE);
    } else {
      this.placeList = [];
      this.displayPlaceLayout = false;
      this.locationNotFound = false;
      this.errorIcon = false;
      this.errorLocationCode.emit(ErrorLocation.EMPTY_ADDRESS);
    }
  }

  filterPredefinedPlaces(searchText: string) {
    if (searchText) {
      this.placeList = this.allPredefinedPlaces.filter(place =>
        place.fullAddress.toLowerCase().includes(searchText.toLowerCase())
      );
    } else {
      this.placeList = [...this.allPredefinedPlaces];
    }

    if (this.placeList.length === 0) {
      this.displayPlaceLayout = false;
      this.locationNotFound = true;
      this.errorIcon = true;
    } else {
      this.displayPlaceLayout = true;
      this.locationNotFound = false;
      this.errorIcon = false;
    }
  }

  async addressInputSubmit() {
    try {
      this.stopDebounce = true;
      if (this.addressType === AddrSysFlag.Predefined) {
        this.filterPredefinedPlaces(this.addressSearchValue);
      } else {
        await this.getPlace(this.addressSearchValue);
      }
    } catch (error) {
      this.stopDebounce = false;
    }
  }
}
